import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ChevronLeft } from 'lucide-react'
import { adminApi } from '@/utils/api'

type UserAssessment = {
  assessmentId: string
  coupleId: string
  contractNumber: string
  organizationName: string
  attemptNumber: number
  status: string
  currentSection: number
  totalSections: number
  progressPercentage: number
  scores?: Record<string, number>
  riskLevels?: Record<string, string>
  totalResponses: number
  startedAt?: string
  completedAt?: string | null
}

type UserProfile = {
  id: string
  firstName: string
  lastName: string
  email: string
  gender: string
  role: string
  organizationName: string
}

type UserAssessmentsData = {
  user: UserProfile
  totalAssessments: number
  assessments: UserAssessment[]
}

const statusBadge = (status?: string) => {
  const s = (status || '').toLowerCase().replace(/[-\s]/g, '_')
  if (s === 'completed') return 'bg-[#d1f4e0] text-[#15803d]'
  if (s === 'in_progress') return 'bg-[#fef3c7] text-[#d97706]'
  return 'bg-[#ffedd5] text-[#ea580c]'
}

const riskColor = (level?: string) => {
  if (!level) return 'text-gray-500'
  const l = level.toLowerCase()
  if (l === 'high') return 'text-red-600 font-semibold'
  if (l === 'moderate') return 'text-[#FF6600] font-semibold'
  return 'text-[#15803d]'
}

const formatDate = (d?: string | null) => {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<UserAssessmentsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setError('Missing user ID')
      setLoading(false)
      return
    }
    const fetch = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await adminApi.userAssessments(userId)
        const payload = (res as { data?: UserAssessmentsData }).data || (res as unknown as UserAssessmentsData)
        setData(payload)
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load user assessments')
      } finally {
        setLoading(false)
      }
    }
    void fetch()
  }, [userId])

  if (loading) {
    return (
      <div className='w-full min-h-screen bg-[#edeff3] flex items-center justify-center'>
        <p className='text-[#4e4d4d]'>Loading user details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='w-full min-h-screen bg-[#edeff3] p-4 md:p-6 lg:p-8'>
        <button onClick={() => navigate('/admin/users')} className='flex items-center gap-2 text-[14px] text-[#4e4d4d] hover:text-[#151515] mb-4'>
          <ChevronLeft className='h-4 w-4' /> Back to Users
        </button>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 text-center'>
          <p className='text-red-600'>{error}</p>
        </div>
      </div>
    )
  }

  const profile = data?.user
  const assessments = data?.assessments || []

  return (
    <div className='w-full min-h-screen bg-[#edeff3] p-4 md:p-6 lg:p-8'>
      <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
        {/* Header */}
        <div className='flex items-center gap-4'>
          <button
            onClick={() => navigate('/admin/users')}
            className='w-[32px] h-[32px] bg-[#eaeaea] rounded-full flex items-center justify-center hover:bg-[#d9d9d9]'
          >
            <ChevronLeft className='h-5 w-5' />
          </button>
          <div>
            <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>{profile?.firstName} {profile?.lastName}</h1>
            <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>{profile?.email} &middot; {profile?.role} &middot; {profile?.organizationName}</p>
          </div>
        </div>

        {/* Profile Card */}
        <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
              <p className='text-[13px] text-[#4e4d4d]'>Full Name</p>
              <p className='text-[14px] font-semibold text-[#151515]'>{profile?.firstName} {profile?.lastName}</p>
            </div>
            <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
              <p className='text-[13px] text-[#4e4d4d]'>Email</p>
              <p className='text-[14px] font-semibold text-[#151515]'>{profile?.email}</p>
            </div>
            <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
              <p className='text-[13px] text-[#4e4d4d]'>Gender</p>
              <p className='text-[14px] font-semibold text-[#151515] capitalize'>{profile?.gender || '-'}</p>
            </div>
            <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
              <p className='text-[13px] text-[#4e4d4d]'>Total Assessments</p>
              <p className='text-[24px] font-bold text-[#151515]'>{data?.totalAssessments ?? 0}</p>
            </div>
          </div>
        </div>

        {/* Assessments List */}
        <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
          <h2 className='text-[18px] font-bold text-[#151515] mb-4'>Assessment History</h2>

          {assessments.length === 0 ? (
            <p className='text-[14px] text-[#4e4d4d]'>No assessments found for this user</p>
          ) : (
            <div className='space-y-4'>
              {assessments.map((a) => (
                <div key={a.assessmentId} className='bg-white rounded-[10px] border border-[#e8e8e8] p-5 space-y-4'>
                  {/* Assessment Header */}
                  <div className='flex items-start justify-between'>
                    <div>
                      <div className='flex items-center gap-2'>
                        <span className='text-base font-bold text-[#151515]'>Attempt #{a.attemptNumber}</span>
                        <span className={`px-2 py-0.5 rounded text-xs capitalize ${statusBadge(a.status)}`}>
                          {a.status?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className='text-xs text-[#4e4d4d] mt-1'>
                        {a.contractNumber} &middot; {a.organizationName}
                      </p>
                    </div>
                    <div className='flex gap-2'>
                      <button
                        onClick={() => navigate(`/admin/couples/${a.coupleId}`)}
                        className='text-xs px-3 py-1 rounded-[8px] bg-[#eaeaea] hover:bg-[#d9d9d9] text-[#151515] font-medium'
                      >
                        View Couple
                      </button>
                      <button
                        onClick={() => navigate(`/admin/assessments/${a.assessmentId}`)}
                        className='text-xs px-3 py-1 rounded-[8px] bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold'
                      >
                        View Full Details
                      </button>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className='grid grid-cols-2 md:grid-cols-5 gap-3 text-[14px]'>
                    <div>
                      <p className='text-[13px] text-[#4e4d4d]'>Progress</p>
                      <div className='flex items-center gap-2'>
                        <div className='flex-1 h-[4px] bg-[#4e4d4d] bg-opacity-25 rounded-full'>
                          <div className='h-full bg-[#FF6600] rounded-full' style={{ width: `${a.progressPercentage}%` }} />
                        </div>
                        <span className='text-xs font-bold text-[#101828]'>{a.progressPercentage}%</span>
                      </div>
                    </div>
                    <div>
                      <p className='text-[13px] text-[#4e4d4d]'>Sections</p>
                      <p className='font-semibold text-[#101828]'>{a.currentSection}/{a.totalSections}</p>
                    </div>
                    <div>
                      <p className='text-[13px] text-[#4e4d4d]'>Responses</p>
                      <p className='font-semibold text-[#101828]'>{a.totalResponses}</p>
                    </div>
                    <div>
                      <p className='text-[13px] text-[#4e4d4d]'>Started</p>
                      <p className='font-semibold text-[#101828]'>{formatDate(a.startedAt)}</p>
                    </div>
                    <div>
                      <p className='text-[13px] text-[#4e4d4d]'>Completed</p>
                      <p className='font-semibold text-[#101828]'>{formatDate(a.completedAt)}</p>
                    </div>
                  </div>

                  {/* Scores */}
                  {a.scores && (
                    <div>
                      <p className='text-[13px] text-[#4e4d4d] mb-2'>Scores</p>
                      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2'>
                        {Object.entries(a.scores).map(([key, value]) => (
                          <div key={key} className='flex justify-between bg-[rgba(237,239,243,0.5)] rounded-[8px] px-3 py-1.5 text-xs'>
                            <span className='text-[#475467] capitalize'>{key.replace(/_/g, ' ')}</span>
                            <span className={riskColor(a.riskLevels?.[key])}>
                              {value} ({a.riskLevels?.[key] || '-'})
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
